import { Menu as MenuPrimitive } from '@base-ui/react/menu';
import { cn } from '@/shared/lib';
import type {
  DropdownMenuCheckboxItemProps,
  DropdownMenuItemProps,
  DropdownMenuProps,
  DropdownMenuRadioGroupProps,
  DropdownMenuRadioItemProps,
  DropdownMenuTriggerProps,
} from '@/shared/ui/dropdown-menu/types';
import { dropdownMenuStyles } from '@/shared/ui/dropdown-menu/styles';

/**
 * Root of the dropdown menu surface.
 *
 * Arrow-key navigation, type-ahead, focus trapping and focus restoration all
 * come from the menu primitive. A menu is a single-tab-stop widget: Tab leaves
 * it, and Up/Down/Arrow keys move within it.
 */
export function DropdownMenu({ children, ...rest }: DropdownMenuProps) {
  return <MenuPrimitive.Root {...rest}>{children}</MenuPrimitive.Root>;
}

/** The control that opens the menu and receives focus back when it closes. */
export function DropdownMenuTrigger({
  children,
  className,
  type = 'button',
  ...props
}: DropdownMenuTriggerProps) {
  return (
    <MenuPrimitive.Trigger
      className={cn(dropdownMenuStyles.trigger, className)}
      type={type}
      {...props}
    >
      {children}
    </MenuPrimitive.Trigger>
  );
}

/** An activatable row. Carries `role="menuitem"`. */
export function DropdownMenuItem({
  children,
  className,
  label,
  disabled,
  closeOnClick,
  onClick,
}: DropdownMenuItemProps) {
  return (
    <MenuPrimitive.Item
      className={cn(dropdownMenuStyles.item, className)}
      label={label}
      disabled={disabled}
      closeOnClick={closeOnClick}
      onClick={onClick}
    >
      {children}
    </MenuPrimitive.Item>
  );
}

/**
 * A toggling row. Carries `role="menuitemcheckbox"` with `aria-checked`, so the
 * checked state is announced rather than inferred from a tick glyph.
 */
export function DropdownMenuCheckboxItem({
  children,
  className,
  label,
  disabled,
  closeOnClick,
  onClick,
  checked,
  defaultChecked,
  onCheckedChange,
}: DropdownMenuCheckboxItemProps) {
  return (
    <MenuPrimitive.CheckboxItem
      className={cn(dropdownMenuStyles.item, className)}
      label={label}
      disabled={disabled}
      closeOnClick={closeOnClick}
      onClick={onClick}
      checked={checked}
      defaultChecked={defaultChecked}
      onCheckedChange={onCheckedChange}
    >
      {children}
    </MenuPrimitive.CheckboxItem>
  );
}

/** Marks a radio group as a single-selection set. */
export function DropdownMenuRadioGroup({
  children,
  className,
  value,
  defaultValue,
  onValueChange,
  disabled,
}: DropdownMenuRadioGroupProps) {
  return (
    <MenuPrimitive.RadioGroup
      className={cn(dropdownMenuStyles.group, className)}
      value={value}
      defaultValue={defaultValue}
      onValueChange={onValueChange}
      disabled={disabled}
    >
      {children}
    </MenuPrimitive.RadioGroup>
  );
}

/** A row within a radio group. Carries `role="menuitemradio"`. */
export function DropdownMenuRadioItem({
  children,
  className,
  label,
  disabled,
  closeOnClick,
  onClick,
  value,
}: DropdownMenuRadioItemProps) {
  return (
    <MenuPrimitive.RadioItem
      className={cn(dropdownMenuStyles.item, className)}
      label={label}
      disabled={disabled}
      closeOnClick={closeOnClick}
      onClick={onClick}
      value={value}
    >
      {children}
    </MenuPrimitive.RadioItem>
  );
}